/**
 * Olive hub — tile registry (Phase X.5.1).
 *
 * Tenant-NEUTRAL structure for the hub board: sections, tiles, capability
 * gating, and chip-icon treatment. Deliberately carries NO tenant-specific
 * data (no URLs, no Peace-branded labels) per CLAUDE.md §3 "no
 * tenant-specific data in product code". External tiles reference the
 * Era-A config fill in `hubToolUrls.ts` by `hrefConfigKey`; the resolver
 * pulls label / subheading / url from there. Internal tiles are app
 * features (Resource Directory, AI Tools) whose labels are product names,
 * not tenant identity, so they live here directly.
 *
 * Gating: each tile names a single `requiredCapability`; `tileVisible`
 * checks it through the client capability predicate. Server endpoints
 * re-derive authority independently (the tiles only deep-link; the linked
 * surfaces enforce their own gates). The three hub tier caps
 * (view_staff_tools / view_office_tools / view_exec_tools) + read +
 * use_ai_tools are the gating vocabulary, per the X.5.1 design matrix.
 *
 * Era B: the external-tile set + the config rows move to per-tenant config
 * (see hubToolUrls.ts header); the section/icon/gating scaffold here stays.
 */

import type { WhoamiResponse } from './types';
import {
  canRead,
  canReadClinicalRecord,
  canUseAiTools,
  canViewAssignmentTools,
  canViewStaffTools,
  canViewOfficeTools,
  canViewExecTools,
} from './capabilities';
import { HUB_TOOL_CONFIG, type HubToolKey } from './hubToolUrls';
import { NAV_REGISTRY, type NavSection } from './navSection';

/** Resolve an internal tile's path suffix from the nav registry (UX-7). */
function navSuffix(section: NavSection): string {
  const entry = NAV_REGISTRY.find((e) => e.section === section);
  if (!entry) throw new Error(`hub tile references unknown nav section: ${section}`);
  return entry.suffix;
}

export type HubSectionId = 'clinical' | 'people' | 'documents' | 'administrative' | 'marketing' | 'system';
export type HubSectionIcon = 'heart' | 'users' | 'folder' | 'settings' | 'mail' | 'terminal';

/** The capability vocabulary the hub tiles gate on (subset of Capability).
 *  read_clinical_record gates the Clients tile — the same floor the
 *  client-record surfaces open on; the linked pages enforce their own
 *  narrower gates (the All-clients roster stays view_all_clinical).
 *  assignment_tools is the one ROLE-DERIVED gate (not a matrix capability):
 *  unit leads + data_steward + exec_support + owner, per
 *  canViewAssignmentTools in capabilities.ts — no tier cap expresses that
 *  audience, and the tile only deep-links (no server endpoint to gate). */
export type HubGateCapability =
  | 'read'
  | 'use_ai_tools'
  | 'read_clinical_record'
  | 'view_staff_tools'
  | 'view_office_tools'
  | 'view_exec_tools'
  | 'assignment_tools';

/**
 * Chip-icon treatment. `image` renders a real vendor logo PNG (served from
 * /public/hub-logos) on a cream chip; `dove` / `pepper` are the Peace brand
 * marks. (Pre-launch, vendor tiles used a `monogram` brand-color stand-in;
 * the operator delivered the real logos on 2026-06-03 and they replaced it.)
 *
 * The dove takes an optional `chip` palette: `sage` (default; the dark-sage
 * chip + cream dove) or `heather` (the warm-lavender heather wash + a
 * heather-deep dove — founder-directed for Prospects, 2026-07-25). Always
 * the SAME brand-mark SVG (hubLogos.ts DOVE_SVG, currentColor); only the
 * chip/dove palette varies.
 */
export type HubTileIcon =
  | { kind: 'dove'; chip?: 'sage' | 'heather' } // Peace dove (internal + Care Managers SP; Prospects takes heather)
  | { kind: 'pepper' } // cream chip + SPICE pepper
  | { kind: 'image'; src: string }; // cream chip + vendor logo (public/hub-logos)

interface HubTileBase {
  id: string;
  section: HubSectionId;
  icon: HubTileIcon;
  requiredCapability: HubGateCapability;
}

export interface HubInternalTile extends HubTileBase {
  kind: 'internal';
  /** Tenant-neutral product feature name. */
  label: string;
  subheading: string;
  /**
   * Nav-registry section whose route this tile opens (UX-7). The suffix is
   * resolved from NAV_REGISTRY so route knowledge lives in exactly one
   * place; the tile keeps its own hub-specific label/subheading copy.
   */
  navSection: NavSection;
}

export interface HubExternalTile extends HubTileBase {
  kind: 'external';
  /** Resolves label / subheading / url from HUB_TOOL_CONFIG (Era-A fill). */
  hrefConfigKey: HubToolKey;
}

export type HubTile = HubInternalTile | HubExternalTile;

export interface HubSection {
  id: HubSectionId;
  label: string;
  icon: HubSectionIcon;
  /** Accent hex; tints the section card background, border, and icon chip. */
  accent: string;
  tiles: HubTile[];
}

// Vendor logos are static assets under /public/hub-logos (operator-delivered
// 2026-06-03). Peace-internal tiles use the dove; SPICE uses the pepper.
// Attio / GitHub / Azure Portal (added later) use the dove as a temporary
// stand-in — no PNG shipped for them; see the 'system' section note below.
export const HUB_SECTIONS: ReadonlyArray<HubSection> = [
  {
    id: 'clinical',
    label: 'Clinical & Client Tools',
    icon: 'heart',
    accent: '#1D9E75',
    tiles: [
      {
        // The Clients entry — the record-page way in. Gated
        // read_clinical_record, the same floor the record page itself opens
        // on; the All-clients roster behind it keeps its own narrower gate.
        id: 'clients',
        kind: 'internal',
        section: 'clinical',
        label: 'Clients',
        subheading: 'Your clients and their records.',
        navSection: 'clients',
        icon: { kind: 'dove' },
        requiredCapability: 'read_clinical_record',
      },
      {
        id: 'resource-directory',
        kind: 'internal',
        section: 'clinical',
        label: 'Resource Directory',
        subheading: 'NJ aging-care resource directory.',
        navSection: 'directory',
        icon: { kind: 'dove' },
        requiredCapability: 'read',
      },
      {
        id: 'ai-tools',
        kind: 'internal',
        section: 'clinical',
        label: 'Writing',
        subheading: 'Draft emails, assessments, and branded documents in the Peace voice.',
        navSection: 'ai',
        icon: { kind: 'dove' },
        requiredCapability: 'use_ai_tools',
      },
    ],
  },
  {
    // People and the office: the Team directory (internal) fronts it. Every
    // internal seat holds the Level 1 floor, so every internal seat can look
    // a colleague up; the directory's own server read tiers what it shows.
    id: 'people',
    label: 'People & Office',
    icon: 'users',
    accent: '#7C6BA8',
    tiles: [
      {
        id: 'team',
        kind: 'internal',
        section: 'people',
        label: 'Team',
        subheading: 'Who is who: photos, roles, units, and work contacts.',
        navSection: 'team',
        icon: { kind: 'dove' },
        requiredCapability: 'view_staff_tools',
      },
    ],
  },
  {
    id: 'documents',
    label: 'Company Documents',
    icon: 'folder',
    accent: '#0078D4',
    tiles: [
      {
        id: 'sp-care-managers',
        kind: 'external',
        section: 'documents',
        hrefConfigKey: 'sp_care_managers',
        icon: { kind: 'dove' },
        requiredCapability: 'view_staff_tools',
      },
      {
        id: 'sp-spice',
        kind: 'external',
        section: 'documents',
        hrefConfigKey: 'sp_spice',
        icon: { kind: 'pepper' },
        requiredCapability: 'view_staff_tools',
      },
      {
        id: 'sp-office',
        kind: 'external',
        section: 'documents',
        hrefConfigKey: 'sp_office',
        icon: { kind: 'image', src: '/hub-logos/sharepoint.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        // Prospects folder (PeaceOffice site, Intake/Prospects) — the
        // intake-assignment audience only: unit leads + data steward +
        // exec support + owner. Role-derived gate; see HubGateCapability.
        // Chip: the Peace dove on the heather (lavender) wash, not the
        // SharePoint logo (founder-directed 2026-07-25). Heather is also
        // the intake task-category family (taskCategories.ts), so the
        // Prospects tile reads as intake at a glance.
        id: 'sp-prospects',
        kind: 'external',
        section: 'documents',
        hrefConfigKey: 'sp_prospects',
        icon: { kind: 'dove', chip: 'heather' },
        requiredCapability: 'assignment_tools',
      },
      {
        id: 'sp-executive',
        kind: 'external',
        section: 'documents',
        hrefConfigKey: 'sp_executive',
        icon: { kind: 'image', src: '/hub-logos/sharepoint.png' },
        requiredCapability: 'view_exec_tools',
      },
    ],
  },
  {
    id: 'administrative',
    label: 'Administrative Tools',
    icon: 'settings',
    accent: '#C4A265',
    tiles: [
      {
        id: 'ramp',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'ramp',
        icon: { kind: 'image', src: '/hub-logos/ramp.png' },
        requiredCapability: 'view_staff_tools',
      },
      {
        id: 'rippling',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'rippling',
        icon: { kind: 'image', src: '/hub-logos/rippling.png' },
        requiredCapability: 'view_staff_tools',
      },
      {
        id: 'bill',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'bill',
        icon: { kind: 'image', src: '/hub-logos/bill.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        id: 'quickbooks',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'quickbooks',
        icon: { kind: 'image', src: '/hub-logos/quickbooks.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        id: 'chase',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'chase',
        icon: { kind: 'image', src: '/hub-logos/chase.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        id: 'faxage',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'faxage',
        icon: { kind: 'image', src: '/hub-logos/faxage.png' },
        // Every clinical seat faxes: staff access, as Amazon Business.
        requiredCapability: 'view_staff_tools',
      },
      {
        id: 'amazon-business',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'amazon_business',
        icon: { kind: 'image', src: '/hub-logos/amazon-business.png' },
        requiredCapability: 'view_staff_tools',
      },
      {
        // Rides booked for clients. Staff access includes care managers;
        // Directory editors without the staff capability do not see it.
        id: 'uber-health',
        kind: 'external',
        section: 'administrative',
        hrefConfigKey: 'uber_health',
        icon: { kind: 'image', src: '/hub-logos/uber-health.png' },
        requiredCapability: 'view_staff_tools',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing Tools',
    icon: 'mail',
    accent: '#8B3DFF',
    tiles: [
      {
        // Referral-partner CRM — fronts the section (founder-directed).
        id: 'attio',
        kind: 'external',
        section: 'marketing',
        hrefConfigKey: 'attio',
        icon: { kind: 'image', src: '/hub-logos/attio.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        id: 'mailchimp',
        kind: 'external',
        section: 'marketing',
        hrefConfigKey: 'mailchimp',
        icon: { kind: 'image', src: '/hub-logos/mailchimp.png' },
        requiredCapability: 'view_office_tools',
      },
      {
        id: 'canva',
        kind: 'external',
        section: 'marketing',
        hrefConfigKey: 'canva',
        icon: { kind: 'image', src: '/hub-logos/canva.png' },
        requiredCapability: 'view_office_tools',
      },
    ],
  },
  {
    // System Tools — operator/engineering surfaces, not office tools; both
    // tiles gate on view_exec_tools (not view_office_tools) deliberately.
    // Logos: operator-delivered official marks (2026-07-14), same path as
    // the 2026-06-03 vendor batch; see hubLogos.ts header.
    id: 'system',
    label: 'System Tools',
    icon: 'terminal',
    accent: '#64748B',
    tiles: [
      {
        id: 'github',
        kind: 'external',
        section: 'system',
        hrefConfigKey: 'github',
        icon: { kind: 'image', src: '/hub-logos/github.png' },
        requiredCapability: 'view_exec_tools',
      },
      {
        id: 'azure',
        kind: 'external',
        section: 'system',
        hrefConfigKey: 'azure',
        icon: { kind: 'image', src: '/hub-logos/azure.png' },
        requiredCapability: 'view_exec_tools',
      },
    ],
  },
];

const CAP_PREDICATES: Record<
  HubGateCapability,
  (whoami: WhoamiResponse | null) => boolean
> = {
  read: canRead,
  use_ai_tools: canUseAiTools,
  read_clinical_record: canReadClinicalRecord,
  view_staff_tools: canViewStaffTools,
  view_office_tools: canViewOfficeTools,
  view_exec_tools: canViewExecTools,
  assignment_tools: canViewAssignmentTools,
};

/** True if the logged-in user's capabilities admit this tile. */
export function tileVisible(tile: HubTile, whoami: WhoamiResponse | null): boolean {
  return CAP_PREDICATES[tile.requiredCapability](whoami);
}

export interface ResolvedHubTile {
  id: string;
  label: string;
  subheading: string;
  href: string;
  /** External tiles open in a new tab; internal tiles route in-app. */
  isExternal: boolean;
  icon: HubTileIcon;
}

/**
 * Resolve a registry tile against the live tenant slug + config fill into
 * the flat shape the UI renders. Internal tiles build `/t/{slug}{route}`;
 * external tiles pull label / subheading / url from HUB_TOOL_CONFIG.
 */
export function resolveTile(tile: HubTile, slug: string): ResolvedHubTile {
  if (tile.kind === 'internal') {
    return {
      id: tile.id,
      label: tile.label,
      subheading: tile.subheading,
      href: `/t/${slug}${navSuffix(tile.navSection)}`,
      isExternal: false,
      icon: tile.icon,
    };
  }
  const cfg = HUB_TOOL_CONFIG[tile.hrefConfigKey];
  return {
    id: tile.id,
    label: cfg.label,
    subheading: cfg.subheading,
    href: cfg.url,
    isExternal: true,
    icon: tile.icon,
  };
}
